/* HymnRow — one hymn line with 👍/👎 controls. Thumbs-down requests a replacement
   via POST /api/sundaybuilder/hymn/regenerate. Used inside SundayBuilder™. */

import React, { useState } from "react";
import axios from "axios";
import { ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import "./HymnRow.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HymnRow({ jobId, serviceIndex, hymnIndex, hymn, onReplaced }) {
  const [h, setH] = useState(hymn);
  const [verdict, setVerdict] = useState(null);        // "up" | "down"
  const [regenerating, setRegenerating] = useState(false);
  const [regenErr, setRegenErr] = useState(null);

  const canAct = !!jobId;

  const sendFeedback = async (v) => {
    if (!canAct) return;
    setVerdict(v);
    try {
      await axios.post(`${API}/sundaybuilder/hymn/feedback`, {
        job_id: jobId, service_index: serviceIndex, hymn_index: hymnIndex, verdict: v,
      });
    } catch {
      // Non-blocking — feedback is a signal, not a gate. Rollback the UI if it fails.
      setVerdict(null);
    }
    if (v === "down") regenerate();
  };

  const regenerate = async () => {
    if (!canAct || regenerating) return;
    setRegenerating(true); setRegenErr(null);
    try {
      const r = await axios.post(`${API}/sundaybuilder/hymn/regenerate`, {
        job_id: jobId, service_index: serviceIndex, hymn_index: hymnIndex,
      });
      setH(r.data.hymn);
      setVerdict(null);
      if (onReplaced) onReplaced(r.data.hymn);
    } catch (e) {
      setRegenErr(e.response?.data?.detail || "Could not generate a replacement. Please try again.");
    }
    setRegenerating(false);
  };

  return (
    <li className="hymn-row" data-testid={`hymn-row-${serviceIndex}-${hymnIndex}`}>
      <div className="hymn-main">
        <span className="hymn-title" data-testid={`hymn-title-${serviceIndex}-${hymnIndex}`}>
          {h.placement ? <b>{h.placement}: </b> : null}
          {h.title}
          {h.tune ? <span className="hymn-tune"> ({h.tune})</span> : null}
        </span>
        {h.hymnal_source_id && h.number != null && h.availability_verified && (
          <span className="hymn-source" style={{ fontWeight: 500 }}>
            {" "}— {h.hymnal_source_id} #{h.number}
          </span>
        )}
        {!h.hymnal_source_id && h.source && h.source !== "verified_crosswalk" && h.source !== "no_verified_selection" && (
          <span className="hymn-source"> — {h.source}</span>
        )}
        {h.reason && <span className="hymn-reason">{h.reason}</span>}

        <div className="hymn-actions" data-testid={`hymn-actions-${serviceIndex}-${hymnIndex}`}>
          <button
            className={`hymn-vote ${verdict === "up" ? "on" : ""}`}
            onClick={() => sendFeedback("up")}
            disabled={!canAct || regenerating}
            title="Keep"
            data-testid={`hymn-vote-up-${serviceIndex}-${hymnIndex}`}
          >
            <ThumbsUp size={13} />
          </button>
          <button
            className={`hymn-vote ${verdict === "down" ? "on down" : ""}`}
            onClick={() => sendFeedback("down")}
            disabled={!canAct || regenerating}
            title="Show another"
            data-testid={`hymn-vote-down-${serviceIndex}-${hymnIndex}`}
          >
            {regenerating ? <Loader2 size={13} className="spin" /> : <ThumbsDown size={13} />}
          </button>
        </div>
      </div>

      {regenErr && <div className="hymn-err" data-testid={`hymn-regen-err-${serviceIndex}-${hymnIndex}`}>{regenErr}</div>}
    </li>
  );
}
